import { PixiSettings } from "./pixiSettings";

/**
 * Centralized texture configuration system for responsive components
 * Configuration objects are used with getResponsiveConfig from DimensionsContext
 */

// Centralized font family configuration
export const FONT_CONFIG = {
  PRIMARY: '"Oxanium", sans-serif',
  SECONDARY: "Arial", // Fallback/secondary font if needed
  TERTIARY: '"Noto Sans", sans-serif', // Noto Sans for specific UI elements
};

// Centralized text styling configuration
export const TEXT_STYLE_CONFIG = {
  // Outline properties for text elements
  OUTLINE: {
    stroke: "rgba(0, 0, 0, 0.2)", // Black outline with 0.5 opacity
    strokeThickness: 1, // Outline thickness in pixels
  },
};

// Shared toolbar buttons configuration (Home, Fullscreen, Sound, Menu)
export const TOOLBAR_BUTTON_CONFIG = {
  BASE_WIDTH: 36,
  BASE_HEIGHT: 36,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.4,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.25,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.35,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.25,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Play button configuration
export const PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.7,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.6,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1,
    },
  },
};

// Autoplay/Bet Select button configuration (shared by both buttons)
export const AUTOPLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 78,
  BASE_HEIGHT: 78,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.6,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.35,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.1,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Company Logo configuration
export const COMPANY_LOGO_CONFIG = {
  BASE_WIDTH: 100, // PixiSettings.LOGO_WIDTH (reduced from 140)
  BASE_HEIGHT: 27, // PixiSettings.LOGO_HEIGHT (proportionally reduced from 38)

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.4,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.25,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.35,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.25,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Bet Increment/Decrement buttons configuration
export const BET_CONTROL_BUTTON_CONFIG = {
  BASE_WIDTH: 36,
  BASE_HEIGHT: 36,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.4,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.2,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Sound button configuration
export const USER_INTERFACE_BUTTON_CONFIG = {
  BASE_WIDTH: 58,
  BASE_HEIGHT: 58,

  // 45,38,60,45,58

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.05,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.225,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

export const SLIDER_BUTTON_CONFIG = {
  BASE_WIDTH: 82,
  BASE_HEIGHT: 82,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.25,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.0,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.25,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.0,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Buy Free Spins button configuration
export const BUY_BUTTON_CONFIG = {
  BASE_WIDTH: 84, // PixiSettings.BUY_BUTTON_SIZE
  BASE_HEIGHT: 84, // PixiSettings.BUY_BUTTON_SIZE

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.5,
        xOffsetMultiplier: 1.0,
        yOffsetMultiplier: 2.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.25,
        xOffsetMultiplier: 2.5,
        yOffsetMultiplier: 2.5,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.0,
        xOffsetMultiplier: 1.0,
        yOffsetMultiplier: 2.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.0,
        xOffsetMultiplier: 1.0,
        yOffsetMultiplier: 2.5,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
      xOffsetMultiplier: 1.0,
      yOffsetMultiplier: 2.5,
    },
  },
};

// Animation timing configuration (centralized fade in/out durations)
export const ANIMATION_TIMINGS = {
  // Loader popup animations
  LOADER_FADE_IN: 300, // Fade in overlay duration
  LOADER_FADE_OUT: 300, // Fade out loader duration

  // Intro popup animations
  INTRO_POPUP_FADE_OUT: 400, // Fade out intro popup duration

  // Background animation
  BACKGROUND_BLUR_FADE: 250, // Background blur fade out duration

  // Win animation timings
  SPINE_ANIMATION_DURATION: 3000, // Each win spine animation lasts exactly 3 seconds
};

// Divider spine configuration
export const DIVIDER_CONFIG = {
  // Scaling multipliers
  widthMultiplier: 3.6,
  heightMultiplier: 1.0,

  // Position offset (positive = right, negative = left)
  xOffsetMultiplier: 0.125,
};

// Text Button configuration
export const TEXT_BUTTON_CONFIG = {
  // Device-specific configurations for text button font sizing and positioning
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        fontSizeMultiplier: 1.35,
        yOffsetMultiplier: 0.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        fontSizeMultiplier: 1.25,
        yOffsetMultiplier: 0.25,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        fontSizeMultiplier: 1.85,
        yOffsetMultiplier: 0.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        fontSizeMultiplier: 1.5,
        yOffsetMultiplier: 0.35,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      fontSizeMultiplier: 1.0,
      yOffsetMultiplier: 0.25,
    },
  },
};

// Win Display configuration
export const WIN_DISPLAY_CONFIG = {
  BASE_FONT_SIZE: 38,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        fontSizeMultiplier: 1.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        fontSizeMultiplier: 1.3,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        fontSizeMultiplier: 1.0,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        fontSizeMultiplier: 1.0,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      fontSizeMultiplier: 1.0,
    },
  },
};

// Message Display configuration
export const MESSAGE_DISPLAY_CONFIG = {
  BASE_FONT_SIZE: 34,
  BASE_PADDING: 8,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        fontSizeMultiplier: 1.5,
        paddingMultiplier: 2.2,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        fontSizeMultiplier: 1.35,
        paddingMultiplier: 1.5,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        fontSizeMultiplier: 1.4,
        paddingMultiplier: 1.6,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        fontSizeMultiplier: 1.25,
        paddingMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      fontSizeMultiplier: 1.0,
      paddingMultiplier: 1.0,
    },
  },
};
